import { useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import { ChevronLeftIcon } from '@heroicons/react/20/solid'

const MIN_ZOOM = 0.25
const MAX_ZOOM = 3
const ZOOM_STEP = 0.1

function clampZoom(zoom) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))
}

function canElementAbsorbWheel(element, deltaX, deltaY) {
    const style = window.getComputedStyle(element)
    const overflowX = style.overflowX
    const overflowY = style.overflowY
    const canScrollX = (overflowX === 'auto' || overflowX === 'scroll') && element.scrollWidth > element.clientWidth + 1
    const canScrollY = (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight + 1

    if (!canScrollX && !canScrollY) return false

    const scrollingLeft = deltaX < 0
    const scrollingRight = deltaX > 0
    const scrollingUp = deltaY < 0
    const scrollingDown = deltaY > 0

    const atLeft = element.scrollLeft <= 0
    const atRight = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1
    const atTop = element.scrollTop <= 0
    const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1

    const absorbX = canScrollX && ((scrollingLeft && !atLeft) || (scrollingRight && !atRight))
    const absorbY = canScrollY && ((scrollingUp && !atTop) || (scrollingDown && !atBottom))

    if (Math.abs(deltaX) >= Math.abs(deltaY)) {
        return absorbX
    }

    return absorbY || absorbX
}

function findWheelScrollableAncestor(startElement, rootElement, deltaX, deltaY) {
    let node = startElement instanceof Element ? startElement : startElement?.parentElement

    while (node && node !== rootElement) {
        if (canElementAbsorbWheel(node, deltaX, deltaY)) {
            return node
        }
        node = node.parentElement
    }

    return null
}

function isOverCanvasBlockScroll(startElement, rootElement) {
    const start = startElement instanceof Element ? startElement : startElement?.parentElement
    return Boolean(start?.closest?.('[data-canvas-block-scroll]') && rootElement.contains(start))
}

export default function ProgramEditorCanvasControlsOverlay({
    viewportRef,
    transform,
    setTransform,
    programEditorTabs = [],
    selectedProgramEditorTab = 'workouts',
    setSelectedProgramEditorTab = () => {},
}) {

    const navigate = useNavigate()
    const { urlProgramId } = useParams()

    const zoomAtPoint = useCallback((nextZoom, clientX, clientY) => {
        const viewport = viewportRef?.current
        if (!viewport) return

        const rect = viewport.getBoundingClientRect()
        const pointX = clientX - rect.left
        const pointY = clientY - rect.top

        setTransform((current) => {
            const zoom = clampZoom(nextZoom)
            const scale = zoom / current.zoom

            return {
                zoom,
                x: pointX - (pointX - current.x) * scale,
                y: pointY - (pointY - current.y) * scale,
            }
        })
    }, [viewportRef, setTransform])

    const zoomBy = useCallback((delta) => {
        const viewport = viewportRef?.current
        if (!viewport) return

        const rect = viewport.getBoundingClientRect()
        zoomAtPoint(
            transform.zoom + delta,
            rect.left + rect.width / 2,
            rect.top + rect.height / 2,
        )
    }, [viewportRef, transform.zoom, zoomAtPoint])

    useEffect(() => {
        const viewport = viewportRef?.current
        if (!viewport) return

        const onWheel = (event) => {
            // Pinch / ctrl+wheel always zooms toward the cursor.
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault()

                const rect = viewport.getBoundingClientRect()
                const pointX = event.clientX - rect.left
                const pointY = event.clientY - rect.top
                const zoomFactor = Math.exp(-event.deltaY * 0.01)

                setTransform((current) => {
                    const zoom = clampZoom(current.zoom * zoomFactor)
                    const scale = zoom / current.zoom

                    return {
                        zoom,
                        x: pointX - (pointX - current.x) * scale,
                        y: pointY - (pointY - current.y) * scale,
                    }
                })
                return
            }

            // Over an exercise collection: never pan the canvas. Nested scroll
            // keeps working; at the edges the gesture is swallowed.
            if (isOverCanvasBlockScroll(event.target, viewport)) {
                if (!findWheelScrollableAncestor(event.target, viewport, event.deltaX, event.deltaY)) {
                    event.preventDefault()
                }
                return
            }

            // Other nested scroll containers keep native wheel scrolling.
            if (findWheelScrollableAncestor(event.target, viewport, event.deltaX, event.deltaY)) {
                return
            }

            event.preventDefault()

            setTransform((current) => ({
                ...current,
                x: current.x - event.deltaX,
                y: current.y - event.deltaY,
            }))
        }

        viewport.addEventListener('wheel', onWheel, { passive: false })
        return () => viewport.removeEventListener('wheel', onWheel)
    }, [viewportRef, setTransform])

    return (
        <>
            {/* Top Controls */}

            <div
                className="absolute top-4 left-4 z-50"
                onPointerDown={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className="flex items-center gap-x-1 rounded-md border border-zinc-200 bg-white/90 px-3 py-1.5 text-sm text-zinc-600 shadow-sm backdrop-blur hover:bg-white hover:text-zinc-900"
                    // onClick={() => navigate(`/program/${urlProgramId}/playground`)}
                    onClick={() => navigate(-1)}
                >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Back
                </button>
            </div>

            <div
                className="absolute top-4 left-1/2 z-50 -translate-x-1/2"
                onPointerDown={(event) => event.stopPropagation()}
            >
                <div className="flex items-center rounded-md border border-zinc-200 bg-white/90 p-0.5 text-sm shadow-sm backdrop-blur">
                    {programEditorTabs.map((tab, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => setSelectedProgramEditorTab(tab.key)}
                            className={`
                                rounded px-3 py-1.5 
                                ${selectedProgramEditorTab === tab.key ? 'bg-orange-400 text-white' : 'text-zinc-600 hover:bg-white hover:text-zinc-900'}
                            `}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Bottom Controls */}

            <div
                className="absolute bottom-0 right-4 flex items-center gap-1 rounded-md border border-zinc-200 bg-white/90 px-1 py-1 text-xs text-zinc-600 shadow-sm backdrop-blur z-50"
                onPointerDown={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded hover:bg-zinc-100"
                    onClick={() => zoomBy(-ZOOM_STEP)}
                    aria-label="Zoom out"
                >
                    −
                </button>

                <span className="min-w-[3.25rem] text-center tabular-nums">
                    {Math.round(transform.zoom * 100)}%
                </span>

                <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded hover:bg-zinc-100"
                    onClick={() => zoomBy(ZOOM_STEP)}
                    aria-label="Zoom in"
                >
                    +
                </button>
            </div>
        </>
    )
}
